import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useSession } from "@/features/auth";
import { isApiError } from "@/shared/api/errors";
import {
  getMyCartQueryKey,
  guestQuote,
  useMergeCart,
  useMyCart,
  useSetCartQuantity,
} from "@/shared/api/generated/cart-controller/cart-controller";
import type {
  CartItemRequest,
  QuoteResponse,
} from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import { guestCart, type GuestItem } from "./guestCart";

const toCartItem = (item: GuestItem): CartItemRequest => ({
  cl_product_id: item.productId,
  cl_quantity: item.quantity,
  cl_seen_price: item.seenPrice ?? null,
});

/**
 * Whether the guest cart is being moved into the server cart right now (one merge per login, shared by
 * every component): the checkout page waits for it instead of showing an empty cart.
 */
const mergeState = (() => {
  let merging = false;
  const listeners = new Set<() => void>();
  return {
    get: () => merging,
    set: (value: boolean) => {
      merging = value;
      listeners.forEach((listener) => {
        listener();
      });
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
})();

export interface Cart {
  /** Server-priced contents; undefined while loading or when the cart is empty for a guest. */
  quote: QuoteResponse | undefined;
  /** Server cart version (logged-in customers); goes back with the next change and the order. */
  version: number;
  /** Number of pieces, for the header badge. */
  count: number;
  isLoading: boolean;
  /** Loading the cart failed (the page offers a retry). */
  isError: boolean;
  retry: () => void;
  /** A change is being saved: steppers wait so two quick clicks don't race (ADR-0004). */
  isSaving: boolean;
  /** Resolves to false when the change was not saved (the reason is already shown in a toast). */
  setQuantity: (productId: string, quantity: number) => Promise<boolean>;
  /** Puts the product in the cart if it is not there yet («Купить»); false when that failed. */
  ensureInCart: (productId: string) => Promise<boolean>;
}

/**
 * The cart, whoever is looking: logged-in customers get the server cart (versioned, 409 when another
 * tab changed it), guests a cart kept in this browser and priced by the server.
 */
export function useCart(): Cart {
  const { t } = useTranslation("cart");
  const queryClient = useQueryClient();
  const { status } = useSession();
  const authenticated = status === "authenticated";
  const guestItems = useSyncExternalStore(guestCart.subscribe, guestCart.getItems);
  const merging = useSyncExternalStore(mergeState.subscribe, mergeState.get);

  const server = useMyCart({ query: { enabled: authenticated } });
  const guest = useQuery({
    // the seen price goes with the request but is no reason to ask again (remembering it must not refetch)
    queryKey: ["/api/v1/checkout/quote", guestItems.map((i) => [i.productId, i.quantity])],
    queryFn: () => guestQuote({ q_items: guestItems.map(toCartItem) }),
    enabled: !authenticated && guestItems.length > 0,
    placeholderData: keepPreviousData,
  });
  const update = useSetCartQuantity({
    mutation: {
      onSuccess: (cart) => {
        queryClient.setQueryData(getMyCartQueryKey(), cart);
      },
      onError: (error) => {
        if (isApiError(error, "checkout.cart_stale")) {
          toast.info(t("staleReloaded"));
        } else {
          toast.error(t("saveError"));
        }
        void queryClient.invalidateQueries({ queryKey: getMyCartQueryKey() });
      },
    },
  });

  const quote = authenticated
    ? server.data?.c_quote
    : guestItems.length > 0
      ? guest.data
      : undefined;
  // the first priced view of a guest line is the price the guest has seen («цена изменилась» later)
  useEffect(() => {
    if (authenticated || !guest.data) return;
    guestCart.rememberPrices(new Map(guest.data.q_lines.map((l) => [l.cl_product_id, l.cl_price])));
  }, [authenticated, guest.data]);
  const count = authenticated
    ? (server.data?.c_quote.q_lines.reduce((sum, l) => sum + l.cl_quantity, 0) ?? 0)
    : guestItems.reduce((sum, i) => sum + i.quantity, 0);

  const setQuantity = async (productId: string, quantity: number, version?: number) => {
    if (!authenticated) {
      const price = quote?.q_lines.find((l) => l.cl_product_id === productId)?.cl_price;
      guestCart.setQuantity(productId, quantity, price);
      return true;
    }
    return update
      .mutateAsync({
        productId,
        data: {
          cl_quantity: quantity,
          cl_expected_version: version ?? server.data?.c_version ?? 0,
        },
      })
      .then(() => true)
      .catch(() => false); // reported in onError
  };

  const ensureInCart = async (productId: string) => {
    if (!authenticated) {
      if (!guestItems.some((i) => i.productId === productId)) guestCart.setQuantity(productId, 1);
      return true;
    }
    // the cart may not be loaded yet (or is stale): read it before deciding, never guess version 0
    const current = await server.refetch();
    if (!current.data) return false;
    if (current.data.c_quote.q_lines.some((l) => l.cl_product_id === productId)) return true;
    return setQuantity(productId, 1, current.data.c_version);
  };

  return {
    quote,
    version: server.data?.c_version ?? 0,
    count,
    isLoading:
      merging || (authenticated ? server.isPending : guestItems.length > 0 && guest.isPending),
    isError: authenticated ? server.isError : guest.isError,
    retry: () => {
      void (authenticated ? server.refetch() : guest.refetch());
    },
    isSaving: update.isPending,
    setQuantity,
    ensureInCart,
  };
}

/** True while the guest cart is being moved into the server cart after login. */
export function useIsMergingCart(): boolean {
  return useSyncExternalStore(mergeState.subscribe, mergeState.get);
}

/**
 * After login, moves the guest cart from this browser into the server cart (once), so nothing the
 * visitor picked is lost.
 */
export function useMergeGuestCartOnLogin(): void {
  const { t } = useTranslation("cart");
  const queryClient = useQueryClient();
  const { status } = useSession();
  const { mutate } = useMergeCart();
  // one attempt per login: a failing merge must not retry on every render
  const attempted = useRef(false);

  useEffect(() => {
    if (status !== "authenticated") {
      attempted.current = false;
      return;
    }
    const items = guestCart.getItems();
    if (items.length === 0 || attempted.current) return;
    attempted.current = true;
    mergeState.set(true);
    mutate(
      { data: { c_items: items.map(toCartItem) } },
      {
        onSuccess: (cart) => {
          guestCart.clear();
          queryClient.setQueryData(getMyCartQueryKey(), cart);
        },
        onError: () => {
          toast.error(t("mergeError"));
        },
        onSettled: () => {
          mergeState.set(false);
        },
      },
    );
  }, [status, mutate, queryClient, t]);
}
